import { FC } from "react";

import { Box, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";

interface Props {
  body: string;
  headline: string;
}

/**
 * Dark "KEY INSIGHTS" editorial card used at the top of each
 * `<UseCase>SectionResults` when rendered inside `SampleReportModal`
 * (gated by the component's `showSampleEditorialSummary` prop).
 * Internal helper — not part of the section results public API.
 *
 * Lives alongside the section results (rather than in
 * `quick-insights/SampleReportModal.tsx`) because a couple of reports
 * — most notably `PriceElasticitySectionResults` — interpolate values
 * computed from the report data into the headline/body. Owning the
 * card lets each report supply those interpolated strings without
 * threading them back through the modal shell.
 */
const SampleEditorialSummary: FC<Props> = ({ body, headline }) => {
  const { i18n } = useLocalization();

  return (
    <Box
      className="relative rounded-2xl p-6 lg:p-8"
      style={{ backgroundColor: "hsl(222, 24%, 12%)" }}
    >
      <VStack className="gap-3">
        <Text
          className="text-xs font-semibold tracking-widest uppercase"
          style={{
            color: "rgba(255,255,255,0.4)",
            letterSpacing: 2,
          }}
        >
          {i18n.t("useCasePage.sampleReport.keyInsights")}
        </Text>

        <Text
          className="text-2xl lg:text-3xl leading-snug mt-1 [font-family:Newsreader,Georgia,serif]"
          style={{ color: "rgba(255,255,255,0.95)" }}
        >
          {headline}
        </Text>

        <Text
          className="text-sm leading-relaxed mt-2"
          style={{ color: "rgba(255,255,255,0.72)" }}
        >
          {body}
        </Text>
      </VStack>
    </Box>
  );
};

export default SampleEditorialSummary;
